import { useCmsBlock } from '@bluheadless/ui-logic/src/hooks/cms/useCmsBlock'
import { Fade, GlobalStyles, Zoom } from '@mui/material'
import router from 'next/router'
import { bool, number, oneOf } from 'prop-types'
import { useEffect, useState, useCallback } from 'react'
import { useIntl } from 'react-intl'
import ChatIcon from '../../atoms/icons/chat-icon'
import XIcon from '../../atoms/icons/x-icon'
import Block from '../../particles/cms-block/block'
import {
	FabStyled,
	FloatButton,
	Icons,
	LinksWrapper,
	PopperContent,
	PopperStyled,
	Root,
	Subtitle,
	Title,
} from './floating-actions.styled'

const FloatingActions = ({ className, show, position, top, singleWidget, children }) => {
	const [popperOpen, setPopperOpen] = useState(false)
	const [anchorEl, setAnchorEl] = useState(null)

	const handleClick = (event) => {
		if (event) setAnchorEl(event.currentTarget)
		setPopperOpen(!popperOpen)
	}

	const canBeOpen = popperOpen && Boolean(anchorEl)
	const popperId = canBeOpen ? 'transition-popper' : undefined
	const { content: floatingCmsBlock, loading: floatingCmsBlockLoading } = useCmsBlock({
		identifier: 'floating_custom_links',
	})
	const { formatMessage } = useIntl()

	const onRouteChangeStart = useCallback(() => {
		setPopperOpen(false)
	}, [])

	useEffect(() => {
		router.events.on('routeChangeStart', onRouteChangeStart)
		return () => {
			router.events.off('routeChangeStart', onRouteChangeStart)
		}
	}, [onRouteChangeStart])

	return children?.length > 0 ? (
		!singleWidget ? (
			<>
				<Root className={className} show={show} position={position} top={top}>
					<GlobalStyles
						styles={`
								#amazon-connect-chat-widget >div div:nth-of-type(2) {
									display: none;
								}
								@media (max-width: 375px) {
									.connect-customer-interface {
										width: 292px !important;
									}
								}
							`}
					/>
					{children}
				</Root>

				<div id="floating-actions-portal"></div>
			</>
		) : (
			<FloatButton className={className} show={show} position={position} top={top}>
				<GlobalStyles
					styles={`
						#amazon-connect-chat-widget >div div:nth-of-type(2) {
							display: none;
						}
						@media (max-width: 375px) {
							.connect-customer-interface {
								width: 292px !important;
							}
						}
					`}
				/>

				<Zoom in>
					<FabStyled aria-describedby={popperId} color="primary" onClick={handleClick}>
						{popperOpen ? <XIcon /> : <ChatIcon />}
					</FabStyled>
				</Zoom>

				<PopperStyled
					anchorEl={anchorEl}
					id={popperId}
					open={popperOpen}
					placement="top-start"
					popperOptions={{
						strategy: 'fixed',
					}}
					transition
					keepMounted
				>
					{({ TransitionProps }) => (
						<Fade {...TransitionProps} unmountOnExit timeout={250}>
							<PopperContent>
								<Title variant="h4" gutterBottom>
									{formatMessage({ id: 'floating_need_help_title' })}
								</Title>
								<Subtitle gutterBottom variant="subtitle1">
									{formatMessage({ id: 'floating_need_help_subtitle' })}
								</Subtitle>
								<LinksWrapper>
									{!floatingCmsBlockLoading && floatingCmsBlock && (
										<Icons>
											<Block content={floatingCmsBlock} />
										</Icons>
									)}

									{children?.length > 0 && <Icons>{children}</Icons>}

									<div id="floating-actions-portal"></div>
								</LinksWrapper>
							</PopperContent>
						</Fade>
					)}
				</PopperStyled>
			</FloatButton>
		)
	) : null
}

FloatingActions.defaultProps = {
	show: false,
	position: 'left',
	top: 50,
}

FloatingActions.propTypes = {
	show: bool,
	position: oneOf(['left', 'right']),
	top: number,
}

export default FloatingActions
